import {useFragment, useLazyLoadQuery} from 'react-relay'
import {graphql} from 'relay-runtime'
import type {LinkedPullRequest$key} from './__generated__/LinkedPullRequest.graphql'
import {ActionList, ActionMenu, Box, Text} from '@primer/react'
import type {LinkedPullRequests$data, LinkedPullRequests$key} from './__generated__/LinkedPullRequests.graphql'
import {Suspense, memo} from 'react'
import {LABELS} from '../..'
import {LinkedPullRequest} from './LinkedPullRequest'

import type {LinkedPullRequestsQuery} from './__generated__/LinkedPullRequestsQuery.graphql'

type LinkedPullRequestsProps = {
  issueId: string
  isSticky: boolean
}

export const LazyLinkedPullRequests = (props: LinkedPullRequestsProps) => {
  return (
    <Suspense fallback={<></>}>
      <LazyLinkedPullRequestsInternal {...props} />
    </Suspense>
  )
}

const LazyLinkedPullRequestsInternal = memo(function LinkedPullRequests({issueId, isSticky}: LinkedPullRequestsProps) {
  const {node} = useLazyLoadQuery<LinkedPullRequestsQuery>(
    graphql`
      query LinkedPullRequestsQuery($id: ID!) {
        node(id: $id) {
          ... on Issue {
            ...LinkedPullRequests
          }
        }
      }
    `,
    {
      id: issueId,
    },
    {fetchPolicy: 'store-only'},
  )

  if (!node) return null

  return <LinkedPullRequestsInternal issueData={node} isSticky={isSticky} />
})

export const LinkedPullRequestsInternal = ({
  issueData,
  isSticky,
}: {
  issueData: LinkedPullRequests$key
  isSticky: boolean
}) => {
  const data = useFragment(
    graphql`
      fragment LinkedPullRequests on Issue {
        repository {
          nameWithOwner
        }
        linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
          edges {
            node {
              repository {
                nameWithOwner
              }
              state
              ...LinkedPullRequest
            }
          }
        }
      }
    `,
    issueData,
  )
  const {mergedPullRequests, openPullRequests, externalPrsCount, issueRepositoryName, pullRequests} =
    getPullRequests(data)

  const showCondensedState =
    pullRequests.length > 3 || externalPrsCount > 1 || (openPullRequests.length > 0 && mergedPullRequests.length > 0)

  return (
    <>
      {showCondensedState && isSticky && (
        <Text sx={{color: 'fg.muted', fontSize: '12px', pl: 1}}>
          {LABELS.linkedPullRequests(mergedPullRequests.length, openPullRequests.length)}
        </Text>
      )}
      {showCondensedState && !isSticky && (
        <ActionMenu>
          <ActionMenu.Button size="small" variant="invisible" sx={{color: 'fg.default'}}>
            {LABELS.linkedPullRequests(mergedPullRequests.length, openPullRequests.length)}
          </ActionMenu.Button>
          <ActionMenu.Overlay sx={{display: isSticky ? 'none' : 'block'}}>
            <ActionList>
              {mergedPullRequests.length > 0 && (
                <ListRenderer
                  list={mergedPullRequests}
                  text={LABELS.mergedPrs}
                  issueRepositoryName={issueRepositoryName}
                />
              )}
              {openPullRequests.length > 0 && mergedPullRequests.length > 0 ? <ActionList.Divider /> : null}
              {openPullRequests.length > 0 ? (
                <ListRenderer list={openPullRequests} text={LABELS.openPrs} issueRepositoryName={issueRepositoryName} />
              ) : null}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {!showCondensedState && pullRequests.length > 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden', pl: 1}}>
          <Text sx={{fontSize: 0}}>{mergedPullRequests.length > 0 ? LABELS.mergedPrs : LABELS.openPrs}&nbsp;</Text>
          {pullRequests
            .map((pull, i) => (
              <Box key={i} sx={{display: 'flex'}}>
                <LinkedPullRequest pullKey={pull} issueRepositoryName={issueRepositoryName} isSticky={isSticky} />
              </Box>
            ))
            .flatMap(x => [',', x])
            .slice(1)}
        </Box>
      )}
    </>
  )
}

const ListRenderer = ({
  list,
  text,
  issueRepositoryName,
}: {
  list: LinkedPullRequest$key[]
  text: string
  issueRepositoryName: string
}) => {
  return (
    <>
      <ActionList.GroupHeading aria-label={text}>{text}</ActionList.GroupHeading>
      {list.map((pull, i) => (
        <LinkedPullRequest pullKey={pull} issueRepositoryName={issueRepositoryName} isListItem={true} key={i} />
      ))}
    </>
  )
}

const getPullRequests = (data: LinkedPullRequests$data) => {
  const mergedPullRequests: LinkedPullRequest$key[] = []
  const openPullRequests: LinkedPullRequest$key[] = []
  const pullRequests: LinkedPullRequest$key[] = []
  const issueRepositoryName = data.repository.nameWithOwner.toLocaleLowerCase()

  let externalPrsCount = 0

  data.linkedPullRequests?.edges?.flatMap(a => {
    if (!a || !a.node) return
    const isExternal = a.node.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()
    isExternal && externalPrsCount++
    if (a.node.state === 'MERGED') {
      isExternal ? mergedPullRequests.push({...a.node}) : mergedPullRequests.unshift({...a.node})
    } else {
      isExternal ? openPullRequests.push({...a.node}) : openPullRequests.unshift({...a.node})
    }
    isExternal ? pullRequests.push({...a.node}) : pullRequests.unshift({...a.node})
  })

  return {mergedPullRequests, openPullRequests, externalPrsCount, issueRepositoryName, pullRequests}
}

try{ LazyLinkedPullRequests.displayName ||= 'LazyLinkedPullRequests' } catch {}
try{ LazyLinkedPullRequestsInternal.displayName ||= 'LazyLinkedPullRequestsInternal' } catch {}
try{ LinkedPullRequestsInternal.displayName ||= 'LinkedPullRequestsInternal' } catch {}
try{ ListRenderer.displayName ||= 'ListRenderer' } catch {}